<template>
  <CenteredPageContainer :class="$style.page" v-if="isAvailable">
    <div :class="$style.wrapper">
      <h2 :class="$style.title">アプローチ自動代行の設定更新</h2>
      <section :class="$style.explanation">
        <h4 :class="$style.explanation__heading">操作方法</h4>
        <p>
          まず、下記の「削除する」ボタンを押して、現在保存されている自社の設定をすべて削除してください。<br />
          ※この操作は取り消せません。<br />
          次に、CSVファイルを適当な場所で分割して、回数を分けて保存してください。<br />
          大体1000行が目安です。<br />
        </p>
        <FlatButton @click="handleDelete" :class="[$style.explanation__button]"
          >削除する</FlatButton
        >
        <h4 :class="$style.explanation__heading">削除された設定を復元</h4>
        <DolphinSelector
          v-model="selectedRevision"
          :options="revisions"
          placeholder="削除日時"
        />&nbsp;
        <FlatButton @click="rollback" :class="[$style.explanation__button]"
          >復元する</FlatButton
        >
      </section>
      <div :class="$style.drop" @dragover.prevent @drop.prevent="drop">
        <p>csvファイルをドロップしてください</p>
      </div>
      <div :class="$style.select">
        <input type="file" @change="fileChange" ref="file" />
      </div>
      <div v-if="inputSetting" :class="$style.result">
        <div>登録件数： {{ inputSetting.settings.length }}件</div>
        <FlatButton @click="clickSubmitButton">登録する</FlatButton>
      </div>
    </div>
  </CenteredPageContainer>
</template>

<script lang="ts" setup>
import CenteredPageContainer from '@/components/molecules/CenteredPageContainer.vue'
import { useAlert } from '@/libs/alertPortal'
import { captureException as newrelicCaptureException } from '@/newrelic'
import { type CreateAttackListSenderSettingsInput } from '@/types/schema.generated'
import * as Encoding from 'encoding-japanese'
import { computed, ref } from 'vue'
import { mapCsvStringToAttackListSenderSetting } from './mapCsvStringToAttackListSenderSetting'
import FlatButton from '@/components/atoms/FlatButton.vue'
import {
  useDeleteAllCorporationAttackListSenderSettingsMutation,
  useCreateCorporationAttackListSenderSettingsMutation,
  useFetchRevisionsQuery,
  useRollbackAttackListSenderSettingsMutation,
  type AttackListSenderSettingCsvPageViewerQuery,
  AttackListSenderSettingCsvPageViewerDocument,
} from './AttackListSenderSettingCsvPage.generated'
import DolphinSelector from '@/components/atoms/DolphinSelector.vue'
import { formatDateTime } from '@/libs/utilities/formatDate'
import { useRouter } from 'vue-router'
import { useApolloClient } from '@vue/apollo-composable'

const apollo = useApolloClient()

const { data } =
  await apollo.client.query<AttackListSenderSettingCsvPageViewerQuery>({
    query: AttackListSenderSettingCsvPageViewerDocument,
  })

const isAvailable = data.viewer.attackListSender.available

const router = useRouter()

if (!isAvailable) {
  // eslint-disable-next-line fp/no-mutating-methods
  router.push({ name: 'errorNotFound' })
}

const selectedRevision = ref<string>()

const { refetch: refetchRevisions, result } = useFetchRevisionsQuery()
const revisions = computed(() =>
  (result.value?.corporationAttackListSenderSettingRevisions ?? []).map(
    rev => ({
      value: rev.id,
      text: formatDateTime(new Date(rev.createdAt)),
    })
  )
)

const alert = useAlert()
const { mutate: rollbackAttackListSenderSettings } =
  useRollbackAttackListSenderSettingsMutation()
const rollback = async () => {
  const id = selectedRevision.value
  if (id == null) {
    return
  }
  try {
    await rollbackAttackListSenderSettings({ id })
  } catch (e) {
    newrelicCaptureException(e)
    alert.error('復元に失敗しました')
    return
  }
  alert.success('復元に成功しました')
}

const inputSetting = ref<CreateAttackListSenderSettingsInput>()

const drop = async (event: DragEvent) => {
  const file = event.dataTransfer?.files[0]
  if (!file) {
    return
  }
  if (file.type !== 'text/csv') {
    alert.error(`csvファイルのみ使用できます。入力ファイルタイプ: ${file.type}`)
    return
  }

  const arrayBuffer = await file.arrayBuffer()
  const uint8Array = new Uint8Array(arrayBuffer)
  const charset = Encoding.detect(uint8Array)
  // windows機でgoogleスプレッドシートからCSVをダウンロードしたときの対応
  if (charset === 'SJIS') {
    const unicodeArray = Encoding.convert(uint8Array, {
      to: 'UNICODE',
      from: 'SJIS',
    })
    const csvString = Encoding.codeToString(unicodeArray)
    // 処理する
    processCsvString(csvString)
    return
  }

  // 処理する
  processCsvString(await file.text())
}

const fileChange = async (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (!file) {
    return
  }
  if (file.type !== 'text/csv') {
    alert.error(`csvファイルのみ使用できます。入力ファイルタイプ: ${file.type}`)
    return
  }
  const csvString = await file.text()
  // 処理する
  processCsvString(csvString)
}

const processCsvString = (csvString: string) => {
  try {
    inputSetting.value = mapCsvStringToAttackListSenderSetting(csvString)
  } catch (e) {
    if (e instanceof Error) {
      alert.error(e.message)
      return
    }
  }
}

const createCorporationAttackListSenderSettingsMutation =
  useCreateCorporationAttackListSenderSettingsMutation({})

const clickSubmitButton = async () => {
  if (!inputSetting.value) {
    alert.error('ファイルを読み込んでください')
    return
  }

  try {
    await createCorporationAttackListSenderSettingsMutation.mutate({
      input: inputSetting.value,
    })
  } catch (e) {
    console.error(e)

    newrelicCaptureException(e)
    alert.error('登録に失敗しました')
    return
  }

  alert.success('登録しました')
}

const deleteAllCorporationAttackListSenderSettingsMutation =
  useDeleteAllCorporationAttackListSenderSettingsMutation({})

const handleDelete = async () => {
  if (!window.confirm('本当に削除しますか？')) {
    return
  }
  try {
    await deleteAllCorporationAttackListSenderSettingsMutation.mutate()
  } catch (e) {
    console.error(e)

    newrelicCaptureException(e)
    alert.error('削除に失敗しました')
    return
  }
  try {
    await refetchRevisions()
  } catch (e) {
    newrelicCaptureException(e)
    alert.error(
      '削除には成功しましたがデータの取得に失敗しました。ページを読み込み直してください'
    )
    return
  }
  alert.success('削除しました')
}
</script>

<style lang="scss" module>
.page {
  background-color: #ffffff;
  padding: 32px;
  border-radius: $border-radius-v2-rounder;
}

.wrapper {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.explanation {
  background-color: $color-surface-accent-primary-light;
  padding: 16px;
  border-radius: $border-radius-rounder;

  &__heading {
    &:nth-child(n + 2) {
      margin-top: 16px;
    }
  }

  &__button {
    margin-top: 12px;
  }
}

.drop {
  display: flex;
  width: 300px;
  height: 300px;
  border: solid $color-border-medium-emphasis 2px;
  border-radius: $border-radius-rounder;
}

.select {
  margin-top: 20px;
}

.result {
  margin-top: 24px;
}
</style>
